import { ChangeEvent, FC, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grid, TextField, Typography } from '@mui/material';

import { IStepFormProps as IProps } from 'interfaces/auth';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { Modal } from 'components/_basic/Modal';

import styles from './RenameCardTitleModal.module.scss';

interface IRenameCardTitleModalProps extends IProps {
  onChange: (value: string) => void;
  onSubmit: (value: string) => Promise<void>;
  inputValue: string;
  maxLength: number;
  isPending: boolean;
  onClose: VoidFunction;
}

export const RenameCardTitleModal: FC<IRenameCardTitleModalProps> = ({
  onChange,
  onSubmit,
  inputValue,
  onClose,
  isPending,
}) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(inputValue);

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(value);
  };

  const handleSubmit = async () => {
    onChange(localValue);
    await onSubmit(localValue);
  };

  const handleKeydown = async ({ key }: KeyboardEvent<HTMLInputElement>) => {
    if (key === 'Enter' || key === 'Escape') {
      await handleSubmit();
    }
  };

  return (
    <Modal showCloseButton={false} close={onClose}>
      <Box className={styles.docRenameModal}>
        <Grid container alignItems="center" justifyContent="space-between" className={styles.header}>
          <Typography variant="h3" className={styles.title}>
            {t('renameProject')}
          </Typography>
          <Grid item>
            <CloseButton onClick={onClose} />
          </Grid>
        </Grid>
        <TextField onChange={handleChange} onKeyDown={handleKeydown} value={localValue} variant="outlined" />
        <Box className={styles.buttonsWrapper}>
          <Button variant="outlined" size='small' onClick={onClose} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button variant="contained" size='small' onClick={handleSubmit} color="primary" isLoading={isPending}>
            {t('rename')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
